// src/components/RPA/buttons/UploadPopulationButton.js
import React, { useState, useRef } from 'react';
import axios from 'axios';

const api = process.env.REACT_APP_API;

const UploadPopulationButton = ({ docId, disabledHint, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  
  // File upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (file) => {
    // Validate file type
    const allowedTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    if (!allowedTypes.includes(file.type)) {
      setErr(`Unsupported file type: ${file.type}. Please upload Excel, CSV, PDF, or Word documents.`);
      return;
    }

    // Check file size (100MB limit)
    if (file.size > 100 * 1024 * 1024) {
      setErr('File size too large. Please upload files smaller than 100MB.');
      return;
    }

    setSelectedFile(file);
    setErr('');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const uploadPopulation = async () => {
    if (!selectedFile || !docId) return;

    setBusy(true);
    setErr('');
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('populationFile', selectedFile);
      formData.append('docId', docId);

      const res = await axios.post(
        `${api}/admin/rpa/docs/${docId}/population/upload`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          validateStatus: () => true
        }
      );

      if (res.status === 200) {
        setResult(res.data || {});
        if (typeof onDone === 'function') onDone(); // refresh doc list
      } else {
        setErr(res.data?.message || `Upload failed with status ${res.status}`);
      }
    } catch (e) {
      console.error('[RPA] population upload failed:', e);
      setErr('Population upload failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const renderFileSelector = () => {
    if (selectedFile) {
      return (
        <div style={selectedFileBox}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={fileIcon}>📄</div>
            <div>
              <div style={{ fontWeight: 'bold', fontSize: 13 }}>{selectedFile.name}</div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB • {selectedFile.type}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button 
              onClick={uploadPopulation} 
              disabled={busy}
              style={uploadButton}
            >
              {busy ? 'Uploading...' : 'Upload Population'}
            </button>
            <button 
              onClick={() => setSelectedFile(null)}
              style={clearButton}
            >
              Clear
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        style={{
          ...dropZone,
          borderColor: dragOver ? '#3b82f6' : '#d1d5db',
          backgroundColor: dragOver ? '#f0f9ff' : '#fff'
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
      >
        <div style={dropZoneContent}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>📊</div>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
            Upload Population Data
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
            Drag and drop or click to select
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af' }}>
            Supports: Excel (.xlsx, .xls), CSV, PDF with tables, Word documents
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          accept=".xlsx,.xls,.csv,.pdf,.doc,.docx"
          onChange={(e) => {
            const files = Array.from(e.target.files);
            if (files.length > 0) {
              handleFileSelect(files[0]);
            }
          }}
        />
      </div>
    );
  };

  const renderUploadResult = () => {
    if (!result?.populationData) return null;

    const data = result.populationData;
    
    return (
      <div style={resultContainer}>
        <div style={{ marginBottom: 12 }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#1f2937' }}>
            Population Data Successfully Uploaded
          </h4>
        </div>

        {/* Data Summary */}
        <div style={summaryGrid}>
          <div style={summaryItem}>
            <div style={summaryLabel}>File Type</div>
            <div style={summaryValue}>{data.type || 'Unknown'}</div>
          </div>
          <div style={summaryItem}>
            <div style={summaryLabel}>Records</div>
            <div style={summaryValue}>{data.rowCount?.toLocaleString() || 0}</div>
          </div>
          <div style={summaryItem}>
            <div style={summaryLabel}>Columns</div>
            <div style={summaryValue}>{data.headers?.length || 0}</div>
          </div>
          <div style={summaryItem}>
            <div style={summaryLabel}>File Size</div>
            <div style={summaryValue}>{data.size ? `${(data.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown'}</div>
          </div>
        </div>

        {/* Column Headers */}
        {data.headers && data.headers.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <strong style={{ fontSize: 12 }}>Column Headers:</strong>
            <div style={headersContainer}>
              {data.headers.slice(0, 10).map((header, idx) => (
                <span key={idx} style={headerBadge}>
                  {header}
                </span>
              ))}
              {data.headers.length > 10 && (
                <span style={{ ...headerBadge, background: '#f3f4f6', color: '#6b7280' }}>
                  +{data.headers.length - 10} more...
                </span>
              )}
            </div>
          </div>
        )}

        {/* Sample Data Preview */}
        {data.sampleData && data.sampleData.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <strong style={{ fontSize: 12 }}>Sample Data (first 3 rows):</strong>
            <div style={sampleTable}>
              {data.sampleData.slice(0, 3).map((row, idx) => (
                <div key={idx} style={sampleRow}>
                  <span style={rowNumber}>{idx + 1}</span>
                  <span style={rowData}>
                    {typeof row === 'object' 
                      ? Object.values(row).slice(0, 5).join(' | ')
                      : String(row).slice(0, 100)
                    }
                    {(typeof row === 'object' ? Object.values(row).length > 5 : String(row).length > 100) && '...'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Data Structure Info */}
        {data.structure && (
          <div style={{ marginTop: 12, padding: 8, background: '#f0fdf4', borderRadius: 6, border: '1px solid #22c55e' }}>
            <strong style={{ fontSize: 12, color: '#15803d' }}>Data Structure Analysis:</strong>
            <div style={{ fontSize: 11, color: '#15803d', marginTop: 2 }}>
              {data.structure.columns} columns detected • Data types analyzed for testing optimization
            </div>
          </div>
        )}

        <div style={{ marginTop: 12, padding: 8, background: '#f0f9ff', borderRadius: 6, border: '1px solid #3b82f6' }}>
          <strong style={{ fontSize: 12, color: '#1e40af' }}>Next Step:</strong>
          <div style={{ fontSize: 11, color: '#1e40af', marginTop: 2 }}>
            Population data is ready. You can now proceed to "Conduct Testing" to apply the testing attributes to this population.
          </div>
        </div>
      </div>
    );
  };

  if (!open) {
    return (
      <span style={{ display: 'inline-block' }}>
        <button 
          onClick={() => setOpen(true)} 
          disabled={busy || !!disabledHint}
          title={disabledHint || 'Upload population data for testing'}
          style={{ 
            background: '#f59e0b', 
            color: 'white', 
            border: 'none', 
            padding: '4px 8px', 
            borderRadius: 4,
            fontSize: 12,
            cursor: disabledHint ? 'not-allowed' : 'pointer',
            opacity: disabledHint ? 0.6 : 1
          }}
        >
          Upload Population
        </button>
      </span>
    );
  }

  return (
    <span style={{ display: 'inline-block' }}>
      <div style={panel}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <strong>Upload Population Data</strong>
          <button onClick={() => setOpen(false)} style={{ padding: '2px 8px' }}>
            Close
          </button>
        </div>

        {!result && (
          <>
            <div style={{ marginBottom: 12, fontSize: 13, color: '#555' }}>
              Upload the population data that will be tested using the formulated testing attributes. 
              This should contain the complete dataset you want to audit.
            </div>
            
            {renderFileSelector()}
          </>
        )}

        {err && <p style={{ color: 'crimson', marginTop: 12 }}>{err}</p>}

        {result && renderUploadResult()}
      </div>
    </span>
  );
};

/* ------------------------------- styles ---------------------------- */

const panel = {
  position: 'absolute',
  top: '100%',
  left: 0,
  zIndex: 1000,
  marginTop: 4,
  background: '#f8f9fb',
  border: '1px solid #e2e5ea',
  padding: 12,
  borderRadius: 8,
  minWidth: 500,
  maxWidth: 700,
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
};

const dropZone = {
  border: '2px dashed #d1d5db',
  borderRadius: 8,
  padding: 24,
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backgroundColor: '#fff'
};

const dropZoneContent = {
  pointerEvents: 'none'
};

const selectedFileBox = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: 12
};

const fileIcon = {
  fontSize: 24,
  width: 40,
  height: 40,
  borderRadius: 8,
  background: '#f3f4f6',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const uploadButton = {
  background: '#f59e0b',
  color: 'white',
  border: 'none',
  padding: '6px 12px',
  borderRadius: 4,
  fontSize: 12,
  cursor: 'pointer'
};

const clearButton = {
  background: '#f3f4f6',
  color: '#6b7280',
  border: '1px solid #d1d5db',
  padding: '6px 12px',
  borderRadius: 4,
  fontSize: 12,
  cursor: 'pointer'
};

const resultContainer = {
  background: '#fff',
  border: '1px solid #e6e9ef',
  padding: 12,
  borderRadius: 8
};

const summaryGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
  gap: 8
};

const summaryItem = {
  textAlign: 'center'
};

const summaryLabel = {
  fontSize: 10,
  color: '#6b7280',
  textTransform: 'uppercase',
  fontWeight: 'bold',
  marginBottom: 2
};

const summaryValue = {
  fontSize: 14,
  fontWeight: 'bold',
  color: '#1f2937'
};

const headersContainer = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
  marginTop: 4
};

const headerBadge = {
  fontSize: 10,
  padding: '2px 6px',
  borderRadius: 12,
  background: '#eff6ff',
  color: '#1e40af',
  border: '1px solid #3b82f6'
};

const sampleTable = {
  marginTop: 4,
  maxHeight: 120,
  overflowY: 'auto',
  background: '#f8f9fa',
  borderRadius: 4,
  border: '1px solid #e5e7eb'
};

const sampleRow = {
  display: 'flex',
  padding: '4px 8px',
  borderBottom: '1px solid #e5e7eb',
  fontSize: 11,
  fontFamily: 'monospace'
};

const rowNumber = {
  width: 24,
  color: '#6b7280',
  flexShrink: 0
};

const rowData = {
  color: '#374151',
  overflow: 'hidden'
};

export default UploadPopulationButton;